/** 学生页「行规分」面板:总分、系数、记录列表、加减分。
 *  总分只算本学期(term_start 起)的记录;往期记录默认收起,可展开查看(标「往期」)。
 *  删除按钮只在后端判定可删(can_delete)时出现,删除前 window.confirm。 */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useConductConfig, useConductMutations, useStudentConduct } from "../../api/gpa";
import { ConductModal } from "./ClassConductPage";

export function ConductPanel({ studentId, studentName }: { studentId: string; studentName: string }): JSX.Element | null {
  const { t } = useTranslation();
  const d = useStudentConduct(studentId);
  const cfg = useConductConfig();
  const m = useConductMutations();
  const [open, setOpen] = useState(false);
  const [showOld, setShowOld] = useState(false);
  if (!d.data) return null;
  const c = d.data;
  const oldCount = c.records.filter((r) => r.in_term === false).length;
  const shown = showOld ? c.records : c.records.filter((r) => r.in_term !== false);
  const remove = (id: string) => {
    if (!window.confirm(t("gpa.conduct.deleteConfirm"))) return;
    m.remove.mutate(id, { onError: (e) => window.alert((e as Error).message) });
  };
  return (
    <section className="mb-6 rounded-lg border border-slate-200 bg-white p-6" data-testid="conduct-panel">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-600">{t("gpa.conduct.title")}</h2>
        <div className="flex items-center gap-4 text-sm">
          <span>{t("gpa.conduct.total")}: <b>{c.total}</b> <span className="text-xs text-slate-400">({c.base_score} {c.plus ? `+${c.plus}` : ""} {c.minus || ""})</span></span>
          <span>{t("gpa.conduct.coefficient")}: <b className={c.coefficient >= 1 ? "text-emerald-700" : "text-amber-700"}>× {c.coefficient.toFixed(2)}</b></span>
          <button type="button" onClick={() => setOpen(true)} className="rounded bg-sky-500 px-2.5 py-1 text-xs font-medium text-white hover:bg-sky-600">{t("gpa.conduct.add")}</button>
        </div>
      </div>
      {c.term_start && <p className="mb-2 text-xs text-slate-400">{t("gpa.conduct.termSince", { date: c.term_start })}</p>}
      {shown.length === 0 ? <p className="text-xs text-slate-400">{t("gpa.conduct.noRecords")}</p> : (
        <ul className="divide-y divide-slate-100 text-sm">
          {shown.map((r) => (
            <li key={r.id} className={`flex items-center gap-3 py-1.5 ${r.in_term === false ? "opacity-60" : ""}`}>
              <span className="w-24 shrink-0 text-xs text-slate-500">{r.recorded_on}{r.in_term === false && <span className="ml-1 rounded bg-slate-100 px-1 text-[10px] text-slate-500">{t("gpa.conduct.oldTag")}</span>}</span>
              <span className={`w-12 shrink-0 text-right font-medium ${r.points >= 0 ? "text-emerald-700" : "text-rose-600"}`}>{r.points > 0 ? `+${r.points}` : r.points}</span>
              <span className="min-w-0 flex-1 truncate text-slate-700">{r.category}{r.item ? ` · ${r.item}` : ""}{r.note ? <span className="ml-1 text-slate-400">— {r.note}</span> : null}</span>
              <span className="text-xs text-slate-400">{r.teacher_name ?? (r.source === "import" ? t("gpa.conduct.imported") : "")}</span>
              {r.can_delete && <button type="button" onClick={() => remove(r.id)} disabled={m.remove.isPending} className="text-xs text-rose-500 hover:underline disabled:opacity-50">{t("common.delete")}</button>}
            </li>
          ))}
        </ul>
      )}
      {oldCount > 0 && (
        <button type="button" onClick={() => setShowOld((v) => !v)} className="mt-2 text-xs text-sky-600 hover:underline">
          {showOld ? t("gpa.conduct.hideOld") : t("gpa.conduct.showOld", { n: oldCount })}
        </button>
      )}
      {open && cfg.data && (
        <ConductModal student={{ name_zh: studentName }} categories={cfg.data.categories} onClose={() => setOpen(false)}
          onSave={async (r) => { await m.add.mutateAsync({ ...r, student_id: studentId }); setOpen(false); }} />
      )}
    </section>
  );
}
